import { type System, SystemId } from "shared-core/types/system.ts";
import customSystemIconSvg from "../assets/system-logos/custom.svg?raw";

type AlphaNumericalLabel = string & { readonly __brand: "AlnumLabel" };

interface SystemVisualMetadata {
  backgroundColor: string;
  foregroundColor: string;
  label: AlphaNumericalLabel;
}

interface SystemVisualColors {
  backgroundColor: string;
  foregroundColor: string;
}

export type SystemLike =
  Pick<System, "custom_name" | "system_id"> | string | null | undefined;

const DEFAULT_SYSTEM_NAME = "System";
const DEFAULT_SYSTEM_LABEL = "SY";
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const SYSTEM_SVG_LOGOS = import.meta.glob("../assets/system-logos/*.svg", {
  eager: true,
  import: "default",
}) as Record<string, string>;
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const SYSTEM_AVIF_LOGOS = import.meta.glob("../assets/system-logos/*.avif", {
  eager: true,
  import: "default",
}) as Record<string, string>;
const SYSTEM_LOGO_BY_ID = new Map<string, string>();
const SYSTEM_IMAGE_CACHE = new Map<string, string>();
const COLOR_PALETTE: readonly SystemVisualColors[] = [
  { backgroundColor: "#2563EB", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#0F766E", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#DC2626", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#7C3AED", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#EA580C", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#0891B2", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#BE185D", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#15803D", foregroundColor: "#FFFFFF" },
  { backgroundColor: "#F59E0B", foregroundColor: "#111827" },
  { backgroundColor: "#334155", foregroundColor: "#FFFFFF" },
] as const;

function createSystemImageSrc(visual: SystemVisualMetadata): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="System logo">
      <rect width="64" height="64" rx="18" fill="${visual.backgroundColor}" />
      <circle cx="50" cy="14" r="10" fill="${visual.foregroundColor}" opacity="0.12" />
      <circle cx="14" cy="50" r="12" fill="${visual.foregroundColor}" opacity="0.08" />
      <text
        x="32"
        y="36"
        fill="${visual.foregroundColor}"
        font-family="Inter, Arial, sans-serif"
        font-size="22"
        font-weight="700"
        letter-spacing="0.5"
        text-anchor="middle"
      >
        ${visual.label}
      </text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function isSystemRecord(
  system: SystemLike,
): system is Pick<System, "custom_name" | "system_id"> {
  return typeof system === "object" && system !== null && "system_id" in system;
}

function getCustomSystemName(system: SystemLike): string | null {
  if (!isSystemRecord(system)) {
    return null;
  }

  const customName = system.custom_name.trim();

  return customName.length > 0 ? customName : null;
}

function getSystemId(system: SystemLike): string | null {
  if (isSystemRecord(system)) {
    return system.system_id;
  }

  return typeof system === "string" ? system : null;
}

function humanizeSystemId(systemId: string): string {
  return systemId
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getXmlSafeLabel(name: string): AlphaNumericalLabel {
  const normalizedName = name.replace(/[^a-z0-9]/gi, "").toUpperCase();

  return (normalizedName.slice(0, 2) ||
    DEFAULT_SYSTEM_LABEL) as AlphaNumericalLabel;
}

function hashString(value: string): number {
  let hash = 0;

  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }

  return hash;
}

function getColors(value: string): SystemVisualColors {
  return (
    COLOR_PALETTE[hashString(value) % COLOR_PALETTE.length] ?? COLOR_PALETTE[0]!
  );
}

function registerSystemLogos(logos: Record<string, string>): void {
  for (const [path, src] of Object.entries(logos)) {
    const filename = path.split("/").pop();

    if (filename === undefined) {
      continue;
    }

    const systemId = filename.replace(/\.(?:svg|avif)$/u, "");

    if (!SYSTEM_LOGO_BY_ID.has(systemId)) {
      SYSTEM_LOGO_BY_ID.set(systemId, src);
    }
  }
}

registerSystemLogos(SYSTEM_SVG_LOGOS);
registerSystemLogos(SYSTEM_AVIF_LOGOS);

function getSystemLogoSrc(systemId: string): string | null {
  return SYSTEM_LOGO_BY_ID.get(systemId) ?? null;
}

function getGeneratedImageKey(system: SystemLike): string {
  const customName = getCustomSystemName(system);

  if (customName !== null) {
    return `custom:${customName}`;
  }

  return getSystemId(system) ?? DEFAULT_SYSTEM_NAME;
}

function createGeneratedSystemImage(system: SystemLike): string {
  const name = getSystemDisplayName(system);
  const cacheKey = getGeneratedImageKey(system);
  const cachedImage = SYSTEM_IMAGE_CACHE.get(cacheKey);

  if (cachedImage !== undefined) {
    return cachedImage;
  }

  const colors = getColors(cacheKey);
  // Reuse the neutral asset's shape, replacing only its trusted colour values.
  // The custom name influences the palette choice but is never embedded in SVG.
  const imageSrc =
    getSystemId(system) === SystemId.CUSTOM
      ? `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          customSystemIconSvg
            .replace('fill="#F1F1EE"', `fill="${colors.backgroundColor}"`)
            .replace('stroke="#62625E"', `stroke="${colors.foregroundColor}"`),
        )}`
      : createSystemImageSrc({
          ...colors,
          label: getXmlSafeLabel(name),
        });

  SYSTEM_IMAGE_CACHE.set(cacheKey, imageSrc);

  return imageSrc;
}

export function getSystemDisplayName(system: SystemLike): string {
  const customName = getCustomSystemName(system);

  if (customName !== null) {
    return customName;
  }

  const systemId = getSystemId(system);

  if (systemId === null) {
    return DEFAULT_SYSTEM_NAME;
  }

  return humanizeSystemId(systemId);
}

export function getSystemImageAlt(system: SystemLike): string {
  return `${getSystemDisplayName(system)} image`;
}

export function getSystemImageSrc(system: SystemLike): string {
  const systemId = getSystemId(system);

  if (systemId === SystemId.CUSTOM && getCustomSystemName(system) !== null) {
    return createGeneratedSystemImage(system);
  }

  if (systemId !== null) {
    const systemLogoSrc = getSystemLogoSrc(systemId);

    if (systemLogoSrc !== null) {
      return systemLogoSrc;
    }
  }

  return createGeneratedSystemImage(system);
}
